
import CreateAffirmationViewModel from "@models/affirmations/createAffirmationViewModel";
import {
  COVER_IMAGE_RATIO,
} from "@/appSettings";
import Vue from "vue";
import { Component } from "vue-property-decorator";
import AudioGroupViewModel from "@models/meditations/create/audioGroupViewModel";
import MeditationAudioViewModel from "@models/meditations/create/meditationAudioViewModel";
import ImagePreviewModel from "crop-image/src/ImagePreviewModel";
import { mapToCropRequest } from "@/frameworks/imagePreviewModelMapper";
import SearchFilterRequest from "@/api/dataSource/api/Spirit/Admin/Interfaces/Models/Common/SearchFilterRequest";
import PaginationRequest from "@/api/dataSource/api/Common/RequestModels/PaginationRequest";
import SortRequest from "@/api/dataSource/api/Spirit/Common/Extensions/Models/SortRequest";
import { speechErrorMessage, SpeechResult, SpeechStatus } from "@/api/dataSource/api/Spirit/Admin/Services/SpeechService";

@Component
export default class CreateAffirmationComponent extends Vue {
  languages: string[] = [];
  isLoading = false;
  errorMessage = "";
  currentAudioGroup: AudioGroupViewModel = null;
  currentAudioItem: MeditationAudioViewModel = null;
  currentAudioItemGroupIndex = -1;
  isEditAudio = false;
  coverImageRatio = COVER_IMAGE_RATIO;
  savedAudioItemStates: { [id: string]: string } = {};
  speechSaveFailures: MeditationAudioViewModel[] = [];
  staleSpeechIds: { [id: string]: boolean } = {};
  speechGenerationTexts: { [id: string]: string } = {};
  wholeSpeechBusy = false;
  phraseSpeechBusy: { [key: string]: boolean } = {};
  get hasPhraseSpeechJobs(): boolean { return Object.keys(this.phraseSpeechBusy || {}).some(key => this.phraseSpeechBusy[key]); }
  setPhraseSpeechBusy(key: string, busy: boolean) {
    if (busy) this.$set(this.phraseSpeechBusy, key, true);
    else this.$delete(this.phraseSpeechBusy, key);
  }
  get title(): string {
    return this.id ? "Редактирование аффирмации" : "Добавление аффирмации";
  }
  get id(): number {
    const routeId = this.$route.params.id;
    return routeId ? Number(routeId) : this.vm && this.vm.id || 0;
  }
  vm: CreateAffirmationViewModel = new CreateAffirmationViewModel();
  get hasUnsavedSpeechChanges(): boolean {
    return this.getAudioItemsToSave().some(item => !item.id || this.isAudioItemTextChanged(item));
  }
  captureSpeechGenerationTexts() {
    const texts: { [id: string]: string } = {};
    this.vm.audios.forEach(group => group.audios.forEach(item => {
      if (!item.id) return;
      const saved = this.savedAudioItemStates[item.id];
      texts[item.id] = saved ? JSON.parse(saved).description : this.normalizeText(item.description);
    }));
    this.speechGenerationTexts = texts;
  }
  async refreshGeneratedAudio(status: SpeechStatus) {
    try {
      const detail = await this.$api.AffirmationService.getDetailAsync(this.id);
      const files = (detail.Translations || []).flatMap(translation => translation.VoiceFiles || []);
      const panel = this.$refs.speechGeneration as any;
      const allGenerated = !!(panel && panel.ownsTask && !status.failed && !status.isCancelled);
      const refreshedIds: string[] = [];
      // Only refresh audio: the admin may have edited other fields during the job.
      const items = this.vm.audios.flatMap(group => group.audios).concat(this.speechSaveFailures);
      if (this.currentAudioItem) items.push(this.currentAudioItem);
      items.forEach(item => {
        const file = files.find(file => file.Id === item.id);
        if (!file) return;
        item.femaleUrl = file.FemaleUrl;
        item.maleUrl = file.MaleUrl;
        const saved = this.savedAudioItemStates[item.id];
        const savedText = saved ? JSON.parse(saved).description : this.normalizeText(item.description);
        const textUnchanged = Object.prototype.hasOwnProperty.call(this.speechGenerationTexts, item.id)
          && this.speechGenerationTexts[item.id] === savedText;
        if (allGenerated && textUnchanged && file.FemaleUrl && file.MaleUrl) {
          item.speechNeedsUpdate = false;
          this.$delete(this.staleSpeechIds, item.id);
          refreshedIds.push(item.id);
        }
      });
      this.speechSaveFailures = this.speechSaveFailures.filter(item => refreshedIds.indexOf(item.id) === -1);
    } catch (error) {
      this.$notification.error(speechErrorMessage(error));
    }
  }
  async created() {
    this.languages = await this.$cacheProvider.getLanguagesAsync();
    this.vm.initLanguages(this.languages);
    this.vm.id = this.id;
    if (this.$route.params.id) {
      await this.fetchData();
    }
  }
  async fetchData() {
    this.isLoading = true;
    try {
      const res = await this.$api.AffirmationService.getDetailAsync(this.id);
      this.languages = this.mergeLanguages(
        this.languages,
        (res.Translations || []).map((item) => item.LangCode).filter(Boolean)
      );
      this.vm.initLanguages(this.languages);
      this.vm.fromServerModel(res);
      this.vm.audios.forEach(group => group.audios.forEach(item => {
        item.speechNeedsUpdate = !!this.staleSpeechIds[item.id];
      }));
      this.captureAudioItemStates();
    } catch (error) {
      this.$notification.error(error);
    } finally {
      this.isLoading = false;
    }
  }
  mergeLanguages(baseLanguages: string[], extraLanguages: string[]): string[] {
    const languages = baseLanguages.slice();
    const languageKeys = languages.map((langCode) => (langCode || "").toLowerCase());
    extraLanguages.forEach((langCode) => {
      const normalizedLangCode = (langCode || "").toLowerCase();
      if (normalizedLangCode && normalizedLangCode !== "lv" && languageKeys.indexOf(normalizedLangCode) === -1) {
        languages.push(langCode);
        languageKeys.push(normalizedLangCode);
      }
    });
    return languages;
  }
  async save() {
    if (this.isLoading || this.hasPhraseSpeechJobs) return;
    const wasEditing = this.id > 0;
    this.syncImagesFromCroppers();
    const isValid = (this.$refs.validator as any).isValid() && this.validateImages();
    if (!isValid) return;
    if (!this.validateTranslations()) return;
    const audioItems = this.getAudioItemsToSave();
    if (!this.validateAudioLocales(audioItems)) return;
    if (!this.validateSpeechText(audioItems)) return;
    this.isLoading = true;
    try {
      await Promise.all(
        (this.vm.audios || []).flatMap((group) =>
          (group.audios || []).map((item) => item.ensureAudioFileReady())
        )
      );
      const idsBeforeCreate = this.id > 0 || !audioItems.length
        ? []
        : await this.getRecentAffirmationIds();
      this.vm.id = this.id;
      const request = this.vm.toServerModel(false, true);
      request.Back = mapToCropRequest(this.vm.backgroundImage);
      request.Cover = mapToCropRequest(this.vm.coverImage);
      request.Icon = null;
      if (this.id > 0) {
        request.Back = request.Back && request.Back.ImgBase ? request.Back : null;
        request.Cover = request.Cover && request.Cover.ImgBase ? request.Cover : null;
      }
      const res = await this.$api.AffirmationService.updateAsync(request);
      if (res) {
        if (this.id > 0) {
          if (audioItems.length) {
            await this.saveAudioItems(this.id, audioItems);
          }
        } else if (audioItems.length) {
          const createdAffirmationId = await this.findCreatedAffirmationId(idsBeforeCreate);
          if (!createdAffirmationId) {
            this.$notification.error("Аффирмация создана, но не удалось определить id для сохранения аудио");
            return;
          }
          this.vm.id = createdAffirmationId;
          await this.saveAudioItems(createdAffirmationId, audioItems);
        }
        this.$dialog.success(wasEditing ? "Аффирмация успешно обновлена" : "Аффирмация успешно создана");
        this.$router.back();
      }
    } catch (error) {
      this.$notification.error(speechErrorMessage(error));
    } finally {
      this.isLoading = false;
    }
  }
  getAudioItemsToSave(): MeditationAudioViewModel[] {
    return (this.vm.audios || []).flatMap((group) =>
      (group.audios || []).filter((item) =>
        !!(
          item &&
          (
            (!item.id && this.hasAudioItemContent(item)) ||
            item.generateAudio ||
            (item.id && this.savedAudioItemStates[item.id] !== this.getAudioItemState(item))
          )
        )
      )
    );
  }
  captureAudioItemStates() {
    const states: { [id: string]: string } = {};
    (this.vm.audios || []).forEach((group) => {
      (group.audios || []).forEach((item) => {
        if (item && item.id) {
          states[item.id] = this.getAudioItemState(item);
        }
      });
    });
    this.savedAudioItemStates = states;
  }
  getAudioItemState(item: MeditationAudioViewModel): string {
    return JSON.stringify({
      langCode: (item.langCode || "").toLowerCase(),
      name: this.normalizeText(item.name),
      description: this.normalizeText(item.description),
      isPaid: !!item.isPaid,
      audioUrl: item.audioUrl || null,
      base64File: item.base64File || null,
    });
  }
  async getRecentAffirmationIds(): Promise<number[]> {
    const response = await this.$api.AffirmationService.getListAsync(
      new SearchFilterRequest({
        Pagination: new PaginationRequest({
          Skip: 0,
          Take: 20,
        }),
        Sort: new SortRequest({
          FieldName: "Id",
          Desc: true,
        }),
      })
    );
    return (response.Items || []).map((item) => item.Id).filter(Boolean);
  }
  async findCreatedAffirmationId(idsBeforeCreate: number[]): Promise<number> {
    const response = await this.$api.AffirmationService.getListAsync(
      new SearchFilterRequest({
        Pagination: new PaginationRequest({
          Skip: 0,
          Take: 20,
        }),
        Sort: new SortRequest({
          FieldName: "Id",
          Desc: true,
        }),
      })
    );
    const item = (response.Items || []).find(
      (affirmation) => idsBeforeCreate.indexOf(affirmation.Id) === -1
    );
    return item ? item.Id : 0;
  }
  async saveAudioItems(affirmationId: number, audioItems: MeditationAudioViewModel[]) {
    let failed = false;
    for (const item of audioItems) {
      if (!await this.persistAudioItem(item, affirmationId)) failed = true;
    }
    if (failed) throw new Error('Фразы сохранены, но часть озвучки не создана. Повторите озвучку для отмеченных фраз.');
  }
  syncImagesFromCroppers() {
    this.vm.coverImage = this.getCroperImage("coverImageCroper", this.vm.coverImage);
  }
  validateImages(): boolean {
    return ["coverImageCroper"]
      .map((refName) => (this.$refs[refName] as any)?.isValid?.() !== false)
      .every(Boolean);
  }
  getCroperImage(refName: string, fallback: ImagePreviewModel): ImagePreviewModel {
    const image = (this.$refs[refName] as any)?.getFile?.();
    if (image && image.ImageUrl) {
      return image;
    }
    return fallback;
  }
  getFilledTranslationLangCodes(): string[] {
    return (this.vm.translations || [])
      .filter((item) => !!(item && item.hasContent()))
      .map((item) => (item.langCode || "").toLowerCase());
  }
  validateTranslations(): boolean {
    const incompleteTranslation = (this.vm.translations || []).find(
      (item) => item && item.hasAnyContent() && !item.hasContent()
    );
    if (!incompleteTranslation) {
      return true;
    }

    this.$notification.error(
      `Заполните название для языка ${(incompleteTranslation.langCode || "").toUpperCase()}`
    );
    return false;
  }
  hasAudioItemContent(item: MeditationAudioViewModel): boolean {
    return !!(
      item &&
      (
        item.id ||
        item.audioFile ||
        item.base64File ||
        item.audioUrl ||
        this.normalizeText(item.name) ||
        this.normalizeText(item.description)
      )
    );
  }
  validateAudioLocales(items: MeditationAudioViewModel[], showNotification: boolean = true): boolean {
    const filledTranslationLangCodes = this.getFilledTranslationLangCodes();
    const invalidItem = (items || []).find((item) => {
      const langCode = (item && item.langCode || "").toLowerCase();
      return this.hasAudioItemContent(item) && filledTranslationLangCodes.indexOf(langCode) === -1;
    });

    if (!invalidItem) {
      return true;
    }

    if (showNotification) {
      this.$notification.error(
        `Сначала заполните перевод аффирмации для языка ${(invalidItem.langCode || "").toUpperCase()}`
      );
    }
    return false;
  }
  async audioGroupChanged(audioGroup: AudioGroupViewModel) {
    if (audioGroup == null) {
      this.currentAudioGroup = null;
      this.isEditAudio = false;
      return;
    }
    if (this.hasPhraseSpeechJobs) return;
    if (!this.validateSpeechText(audioGroup.audios || [])) return;
    if (this.id > 0 && this.audioAddMode == "add") {
      const itemsToSave = (audioGroup.audios || []).filter(
        (item) => this.hasAudioItemContent(item)
      );

      if (!itemsToSave.length) {
        this.currentAudioGroup = null;
        this.isEditAudio = false;
        this.audioAddMode = null;
        return;
      }
      if (!this.validateAudioLocales(itemsToSave, false)) {
        this.vm.audios.push(audioGroup);
      } else {
        this.isLoading = true;
        try {
          // Keep local items if an ordinary request fails partway through the group.
          this.vm.audios.push(audioGroup);
          for (const item of itemsToSave) {
            await item.ensureAudioFileReady();
            await this.persistAudioItem(item, this.id);
          }
          this.$notification.success("Фразы сохранены");
          await this.fetchData();
        } catch (error) {
          this.$notification.error(speechErrorMessage(error));
        }
      }

    } else if (this.audioAddMode == "edit") {
      Object.assign(this.currentAudioGroup, audioGroup);
    } else if (this.audioAddMode == "add") {
      this.vm.audios.push(audioGroup);
    }
    this.currentAudioGroup = null;
    this.isEditAudio = false;
    this.audioAddMode = null;
    this.isLoading = false;
  }
  audioAddMode: "edit" | "add" | null = null;
  startAddAudio() {
    if (this.hasPhraseSpeechJobs) return;
    this.currentAudioGroup = new AudioGroupViewModel(this.languages);
    this.audioAddMode = "add";
  }
  startEditAudioGroup(item: AudioGroupViewModel) {
    if (this.hasPhraseSpeechJobs) return;
    this.currentAudioGroup = item;
    this.audioAddMode = "edit";
    this.isEditAudio = true;
  }
  startEditAudioItem({
    audioItem,
    groupIndex,
  }: {
    audioItem: MeditationAudioViewModel;
    groupIndex: number;
  }) {
    if (this.hasPhraseSpeechJobs) return;
    this.currentAudioItem = audioItem.clone();
    this.currentAudioItemGroupIndex = groupIndex;
  }
  cancelEditAudioItem() {
    if (this.isLoading || this.phraseSpeechBusy.modal) return;
    this.currentAudioItem = null;
    this.currentAudioItemGroupIndex = -1;
  }
  removeCurrentAudioItemAudio() {
    if (!this.currentAudioItem) {
      return;
    }
    this.currentAudioItem.audioFile = null;
    this.currentAudioItem.base64File = null;
    this.currentAudioItem.audioUrl = null;
  }
  normalizeText(value: string): string | null {
    const text = (value || "").trim();
    return text || null;
  }
  buildAudioItemRequest(item: MeditationAudioViewModel, affirmationId: number) {
    const name = this.normalizeText(item.name) || this.normalizeText(item.description);
    const description = this.normalizeText(item.description);
    return {
      AffirmationId: affirmationId,
      LangCode: item.langCode,
      IsPaid: item.isPaid,
      Id: item.id || null,
      Name: name,
      Description: description,
      Base64Content: item.base64File || null,
      GenerateAudio: item.generateAudio || undefined,
    };
  }
  async saveAudioItem() {
    if (this.isLoading || this.hasPhraseSpeechJobs) return;
    const isValid = (this.$refs.audioItemValidator as any)?.isValid?.() !== false;
    if (!isValid || !this.currentAudioItem || this.currentAudioItemGroupIndex < 0) {
      return;
    }
    if (!this.validateAudioLocales([this.currentAudioItem])) {
      return;
    }
    if (!this.validateSpeechText([this.currentAudioItem])) return;

    this.isLoading = true;
    try {
      await this.currentAudioItem.ensureAudioFileReady();
      const result = await this.persistAudioItem(this.currentAudioItem, this.id);
      if (result) {
        this.$notification.success("Аффирмация сохранена");
        await this.fetchData();
        this.currentAudioItem = null;
        this.currentAudioItemGroupIndex = -1;
      }
    } catch (error) {
      this.$notification.error(speechErrorMessage(error));
    } finally {
      this.isLoading = false;
    }
  }
  validateSpeechText(items: MeditationAudioViewModel[]): boolean {
    if (items.some(item => item.generateAudio && !this.normalizeText(item.description))) {
      this.$notification.error('Для озвучки заполните описание фразы');
      return false;
    }
    return true;
  }
  isAudioItemTextChanged(item: MeditationAudioViewModel): boolean {
    const state = item && this.savedAudioItemStates[item.id];
    return !!state && JSON.parse(state).description !== this.normalizeText(item.description);
  }
  async persistAudioItem(item: MeditationAudioViewModel, affirmationId: number): Promise<boolean> {
    const textChanged = this.isAudioItemTextChanged(item);
    const generate = item.generateAudio;
    try {
      const result = await this.$api.AffirmationService.updateItemAsync(this.buildAudioItemRequest(item, affirmationId));
      if (result) item.id = result;
      if (textChanged && !generate) {
        item.speechNeedsUpdate = true;
        this.$set(this.staleSpeechIds, item.id, true);
        this.$notification.success('Текст сохранён. Озвучьте фразу заново, чтобы обновить запись.');
      }
      if (generate) {
        this.$delete(this.staleSpeechIds, item.id);
        item.speechNeedsUpdate = false;
        this.speechSaveFailures = this.speechSaveFailures.filter(failed => failed.id !== item.id);
      }
      item.generateAudio = false;
      this.$set(this.savedAudioItemStates, item.id, this.getAudioItemState(item));
      this.syncSavedAudioItem(item);
      return true;
    } catch (error) {
      const savedId = error.status === 502 && error.payload && error.payload.voiceFileId;
      if (!savedId) throw error;
      item.id = savedId;
      item.generateAudio = false;
      item.speechNeedsUpdate = true;
      this.$set(this.staleSpeechIds, savedId, true);
      this.$set(this.savedAudioItemStates, savedId, this.getAudioItemState(item));
      this.speechSaveFailures = this.speechSaveFailures.filter(failed => failed.id !== savedId).concat(item);
      this.syncSavedAudioItem(item);
      this.$notification.error(`Фраза сохранена, но озвучка не удалась. ${speechErrorMessage(error)}`);
      return false;
    }
  }
  syncSavedAudioItem(saved: MeditationAudioViewModel) {
    this.vm.audios.forEach(group => group.audios.forEach(item => {
      if (item.id === saved.id && item !== saved) Object.assign(item, saved);
    }));
  }
  speechRetrySucceeded(result: SpeechResult) {
    this.$delete(this.phraseSpeechBusy, 'retry-' + result.voiceFileId);
    this.$delete(this.staleSpeechIds, result.voiceFileId);
    this.speechSaveFailures = this.speechSaveFailures.filter(item => item.id !== result.voiceFileId);
    this.vm.audios.forEach(group => group.audios.forEach(item => {
      if (item.id === result.voiceFileId) {
        item.femaleUrl = result.femaleUrl;
        item.maleUrl = result.maleUrl;
        item.speechNeedsUpdate = false;
      }
    }));
  }
  async startDeleteAudioItem({
    audioItem,
    groupIndex,
  }: {
    audioItem: MeditationAudioViewModel;
    groupIndex: number;
  }) {
    if (this.hasPhraseSpeechJobs) return;
    if (!(audioItem && (audioItem.id || audioItem.name || audioItem.description || audioItem.audioFile || audioItem.audioUrl || audioItem.base64File))) {
      return;
    }

    const shouldDelete = await this.$dialog.confirm(
      "Удалить эту аффирмацию для выбранной локали?"
    );
    if (!shouldDelete) {
      return;
    }
    if (this.hasPhraseSpeechJobs) return;

    if (!audioItem.id) {
      audioItem.name = "";
      audioItem.description = "";
      audioItem.audioFile = null;
      audioItem.base64File = null;
      audioItem.audioUrl = null;
      return;
    }

    this.isLoading = true;
    try {
      const result = await this.$api.AffirmationService.deleteItemAsync({
        Id: audioItem.id,
      });
      if (result) {
        this.$notification.success("Аффирмация удалена");
        await this.fetchData();
      }
    } catch (error) {
      this.$notification.error(error);
    } finally {
      this.isLoading = false;
    }
  }
  deleteAudioGroup(item: AudioGroupViewModel) {
    if (this.hasPhraseSpeechJobs) return;
    this.vm.audios = this.vm.audios.filter((x) => x != item);
  }
}
